import React, { useCallback, useEffect, useState } from 'react';
import {
  fetchCustomers,
  createCustomer,
  updateCustomer,
  deleteCustomer
} from '../../api/customers';

const emptyCustomer = { name: '', phone: '', email: '', address: '', notes: '', discountPercent: 0 };

function parseCsvText(text) {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map((h) => h.trim().toLowerCase().replace(/\s+/g, ''));
  return lines.slice(1).map((line) => {
    const values = line.split(',').map((v) => v.trim());
    const row = {};
    headers.forEach((h, j) => { row[h] = values[j] !== undefined ? values[j] : ''; });
    return row;
  });
}

export default function CustomersManager({ authConfig, onUnauthorized }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [form, setForm] = useState(emptyCustomer);
  const [saving, setSaving] = useState(false);
  const [csvMessage, setCsvMessage] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchCustomers({}, authConfig);
      setList(Array.isArray(data) ? data : []);
      setError('');
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
      else setError('Failed to load customers.');
    } finally {
      setLoading(false);
    }
  }, [authConfig, onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setEditingId(null);
    setShowAddModal(false);
    setForm(emptyCustomer);
  };

  const handleEdit = (item) => {
    setEditingId(item._id);
    setShowAddModal(false);
    setForm({
      name: item.name || '',
      phone: item.phone || '',
      email: item.email || '',
      address: item.address || '',
      notes: item.notes || '',
      discountPercent: item.discountPercent ?? 0
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, discountPercent: Number(form.discountPercent) || 0 };
      if (editingId) {
        await updateCustomer(editingId, payload, authConfig);
      } else {
        await createCustomer(payload, authConfig);
      }
      resetForm();
      await load();
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
      else setError(err?.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this customer?')) return;
    try {
      await deleteCustomer(id, authConfig);
      await load();
      if (editingId === id) resetForm();
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
      else setError(err?.response?.data?.error || 'Failed to delete.');
    }
  };

  const handleCsvImport = async (rows) => {
    let imported = 0;
    for (const r of rows) {
      const name = (r.name || r.customername || '').trim();
      if (!name) continue;
      try {
        await createCustomer({
          name,
          phone: (r.phone || '').trim(),
          email: (r.email || '').trim(),
          address: (r.address || '').trim(),
          notes: (r.notes || '').trim(),
          discountPercent: r.discountpercent ? parseFloat(r.discountpercent) : 0
        }, authConfig);
        imported++;
      } catch (_) {}
    }
    setCsvMessage('Imported ' + imported + ' customers.');
    await load();
  };

  const onFileChange = (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const rows = parseCsvText(ev.target?.result || '');
      handleCsvImport(rows);
    };
    reader.readAsText(file);
  };

  const getInitials = (name = '') => {
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return String(name).slice(0, 2).toUpperCase() || 'C';
  };

  const renderActions = (item) => (
    <div className="flex gap-2">
      <button type="button" onClick={() => handleEdit(item)} className="flex-1 text-sm border px-3 py-2 rounded hover:bg-gray-50">Edit</button>
      <button type="button" onClick={() => handleDelete(item._id)} className="flex-1 text-sm bg-red-600 text-white px-3 py-2 rounded hover:bg-red-700">Delete</button>
    </div>
  );

  const renderForm = ({ submitLabel }) => (
    <form onSubmit={handleSubmit} className="space-y-3 p-5">
      <input className="border w-full p-2 rounded text-sm" placeholder="Name *" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
      <input className="border w-full p-2 rounded text-sm" placeholder="Phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
      <input className="border w-full p-2 rounded text-sm" placeholder="Email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} type="email" />
      <textarea className="border w-full p-2 rounded text-sm" placeholder="Address" value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} rows={2} />
      <input className="border w-full p-2 rounded text-sm" placeholder="Notes" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
      <input className="border w-full p-2 rounded text-sm" type="number" min="0" max="100" step="0.5" placeholder="Discount %" value={form.discountPercent} onChange={(e) => setForm((f) => ({ ...f, discountPercent: e.target.value }))} />
      <div className="flex gap-2 pt-2">
        <button type="submit" disabled={saving} className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded text-sm disabled:opacity-60 hover:bg-indigo-500">{saving ? 'Saving...' : submitLabel}</button>
        <button type="button" onClick={resetForm} className="flex-1 border rounded px-4 py-2 text-sm hover:bg-gray-50">Cancel</button>
      </div>
    </form>
  );

  if (loading) return <p className="text-gray-600">Loading customers...</p>;

  return (
    <div className="space-y-4">
      {error && <p className="text-sm text-red-600">{error}</p>}
      {csvMessage && <p className="text-sm text-green-700">{csvMessage}</p>}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h4 className="font-medium">Customers</h4>
          <p className="text-xs text-gray-500">Manage customer contact and discount details.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => { resetForm(); setShowAddModal(true); }} className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500"><span className="text-lg leading-none">+</span>Add</button>
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 text-sm">
            <button type="button" onClick={() => setViewMode('list')} className={`rounded-md px-3 py-1.5 ${viewMode === 'list' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>List</button>
            <button type="button" onClick={() => setViewMode('grid')} className={`rounded-md px-3 py-1.5 ${viewMode === 'grid' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>Grid</button>
          </div>
        </div>
      </div>
      <div className="flex gap-2 items-center text-sm">
        <span className="text-gray-600">CSV: name, phone, email, address, notes</span>
        <input type="file" accept=".csv" onChange={onFileChange} className="text-sm" />
      </div>
      {list.length === 0 ? (
        <div className="rounded border p-3 text-sm text-gray-500">No customers yet.</div>
      ) : viewMode === 'grid' ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {list.map((item) => (
            <div key={item._id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-4 flex items-start gap-3">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-lg font-bold text-indigo-700">{getInitials(item.name)}</div>
                <div className="min-w-0">
                  <p className="truncate font-semibold text-gray-900">{item.name}</p>
                  <p className="truncate text-sm text-gray-600">{item.phone || item.email || 'No contact'}</p>
                  {item.discountPercent > 0 && <span className="mt-2 inline-flex rounded-full bg-indigo-100 px-2 py-0.5 text-xs text-indigo-700">{item.discountPercent}% off</span>}
                </div>
              </div>
              {renderActions(item)}
            </div>
          ))}
        </div>
      ) : (
        <ul className="border rounded divide-y max-h-80 overflow-y-auto">
          {list.map((item) => (
            <li key={item._id} className="p-3 flex justify-between items-center gap-3">
              <div className="flex min-w-0 items-center gap-3 text-sm">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-sm font-bold text-indigo-700">{getInitials(item.name)}</div>
                <div className="min-w-0">
                  <span className="font-medium">{item.name}</span>
                  {item.phone && <span className="text-gray-500 ml-2">{item.phone}</span>}
                  {item.discountPercent > 0 && <span className="text-indigo-600 ml-2">{item.discountPercent}% off</span>}
                </div>
              </div>
              <div className="w-44 shrink-0">{renderActions(item)}</div>
            </li>
          ))}
        </ul>
      )}

      {showAddModal && !editingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6" role="dialog" aria-modal="true" aria-labelledby="add-customer-title">
          <div className="w-full max-w-md rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <h4 id="add-customer-title" className="font-semibold text-gray-900">Add customer</h4>
              <button type="button" onClick={resetForm} className="text-2xl leading-none text-gray-400 hover:text-gray-700" aria-label="Close add customer">×</button>
            </div>
            {renderForm({ submitLabel: 'Add customer' })}
          </div>
        </div>
      )}

      {editingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6" role="dialog" aria-modal="true" aria-labelledby="edit-customer-title">
          <div className="w-full max-w-md rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <h4 id="edit-customer-title" className="font-semibold text-gray-900">Edit customer</h4>
              <button type="button" onClick={resetForm} className="text-2xl leading-none text-gray-400 hover:text-gray-700" aria-label="Close edit customer">×</button>
            </div>
            {renderForm({ submitLabel: 'Update customer' })}
          </div>
        </div>
      )}
    </div>
  );
}
